import { Alert, Button, CircularProgress, Link, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { PasswordField } from '@/components/forms';
import { ROUTES } from '@/constants/routes';
import { AuthCard } from '@/features/identity/components/AuthCard';
import { AuthHeader } from '@/features/identity/components/AuthHeader';
import { PasswordRequirements } from '@/features/identity/components/PasswordRequirements';
import { PasswordStrengthIndicator } from '@/features/identity/components/PasswordStrengthIndicator';
import { useResetPassword } from '@/features/identity/hooks/useResetPassword';

/** The Reset Password screen's form — `email`/`token` come from the emailed link's query string
 * (read-only, not retyped). */
export function ResetPasswordForm() {
  const { form, onSubmit, serverError, linkIsIncomplete } = useResetPassword();
  const { control, formState } = form;
  const newPassword = form.watch('newPassword');

  return (
    <AuthCard>
      <AuthHeader title="Reset your password" subtitle="Choose a new password for your account." />
      <Stack component="form" onSubmit={onSubmit} spacing={2} noValidate>
        {linkIsIncomplete ? (
          <Alert severity="error">
            This reset link is incomplete or has been altered. Ask for a new one — links are
            single-use and expire quickly.{' '}
            <Link component={RouterLink} to={ROUTES.forgotPassword}>
              Send a new reset link
            </Link>
          </Alert>
        ) : null}
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        <PasswordField
          name="newPassword"
          control={control}
          label="New password"
          autoComplete="new-password"
        />
        <PasswordStrengthIndicator password={newPassword} />
        <PasswordRequirements password={newPassword} />
        <PasswordField
          name="confirmNewPassword"
          control={control}
          label="Confirm new password"
          autoComplete="new-password"
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={formState.isSubmitting || linkIsIncomplete}
          startIcon={
            formState.isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Reset password
        </Button>
      </Stack>
    </AuthCard>
  );
}
